import { useState } from 'react';
import { API } from '../config/config';
import { useAuth } from './useAuth';
import {
  GetReferralCodeUsagesParams,
  PaginatedResponse,
  ReferralCodeUsageResponse,
  ReferralCodeUsageUpdateRequest
} from '../content/applications/Referral/referral.interface';

export const useReferralUsage = () => {
  const { enhancedFetch } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  // GET `/` — List usages (paged)
  const getReferralCodeUsages = async (
    params?: GetReferralCodeUsagesParams
  ): Promise<PaginatedResponse<ReferralCodeUsageResponse> | null> => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (params?.page !== undefined) qs.append('page', String(params.page));
      if (params?.size !== undefined) qs.append('size', String(params.size));
      if (params?.sort) qs.append('sort', params.sort);

      const url = `${API.HOST}/api/v1/referral-code-usages${qs.toString() ? `?${qs.toString()}` : ''}`;

      const res = await enhancedFetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      return res as PaginatedResponse<ReferralCodeUsageResponse>;
    } catch (err: any) {
      const msg = err?.message || 'Failed to list referral code usages';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // GET `/{id}` — Get usage by ID
  const getReferralCodeUsageById = async (
    id: string
  ): Promise<ReferralCodeUsageResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedFetch(
        `${API.HOST}/api/v1/referral-code-usages/${id}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      return res as ReferralCodeUsageResponse;
    } catch (err: any) {
      const msg = err?.message || 'Failed to fetch referral code usage by id';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // GET `/checkout/{checkoutId}` — Get usage by checkout ID
  const getReferralCodeUsageByCheckoutId = async (
    checkoutId: string
  ): Promise<ReferralCodeUsageResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedFetch(
        `${API.HOST}/api/v1/referral-code-usages/checkout/${encodeURIComponent(checkoutId)}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      return res as ReferralCodeUsageResponse;
    } catch (err: any) {
      const msg = err?.message || 'Failed to fetch referral code usage by checkout id';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // PUT `/{id}/status` — Update usage status
  const updateReferralCodeUsageStatus = async (
    id: string,
    body: ReferralCodeUsageUpdateRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await enhancedFetch(
        `${API.HOST}/api/v1/referral-code-usages/${id}/status`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        }
      );
      return true;
    } catch (err: any) {
      const msg = err?.message || 'Failed to update referral code usage status';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    // Actions
    getReferralCodeUsages,
    getReferralCodeUsageById,
    getReferralCodeUsageByCheckoutId,
    updateReferralCodeUsageStatus,

    // State
    loading,
    error,
    clearError
  };
};
